import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import SalarySlipForm from "./components/SalarySlipForm";
import { salarySlipService } from "../../services";
import {
  makeEmptySalarySlipFormValues,
  mapSalarySlipToFormValues,
} from "./utils";
import PageSkeleton from "../../components/ui/PageSkeleton";
import Button from "../../components/ui/Button";
import { ArrowBack } from "@mui/icons-material";

function EditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [salarySlip, setSalarySlip] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingSlip, setLoadingSlip] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const { data } = await salarySlipService.getEmployeesMeta();
        setEmployees(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load employees for salary slip", err);
        toast.error("Unable to load employees");
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, []);

  useEffect(() => {
    // Don't make API call if id is undefined
    if (!id || id === "undefined") {
      toast.error("Invalid salary slip ID");
      navigate("/salary-slip");
      setLoadingSlip(false);
      return;
    }

    const loadSalarySlip = async () => {
      try {
        const { data } = await salarySlipService.get(id);
        setSalarySlip(data);
      } catch (err) {
        console.error("Failed to load salary slip", err);
        toast.error("Salary slip not found");
        navigate("/salary-slip");
      } finally {
        setLoadingSlip(false);
      }
    };

    loadSalarySlip();
  }, [id, navigate]);

  const initialValues = useMemo(() => {
    if (!salarySlip) {
      return makeEmptySalarySlipFormValues();
    }
    return mapSalarySlipToFormValues(salarySlip);
  }, [salarySlip]);

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      const { data } = await salarySlipService.update(id, payload);
      toast.success("Salary slip updated");
      const slipId = data?.id || data?._id || id;
      navigate(`/salary-slip/${slipId}`);
    } catch (err) {
      console.error("Failed to update salary slip", err);
      const message =
        err?.response?.data?.error || "Failed to update salary slip";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const isLoading = loadingEmployees || loadingSlip;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="secondary"
              onClick={() => navigate(`/salary-slip/${id}`)}
              className="flex items-center gap-2"
            >
              <ArrowBack className="w-5 h-5" />
              Back
            </Button>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">
            Edit Salary Slip
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Update salary slip information</p>
        </div>

        {isLoading ? (
          <PageSkeleton type="form" />
        ) : employees.length === 0 || !salarySlip ? (
          <div className="card text-center py-12">
            <p className="text-gray-500 text-lg">
              Unable to load required data. Please try again later.
            </p>
          </div>
        ) : (
          <SalarySlipForm
            initialValues={initialValues}
            employees={employees}
            onSubmit={handleSubmit}
            onCancel={() => navigate(`/salary-slip/${id}`)}
            submitLabel="Update Salary Slip"
            loading={saving}
          />
        )}
      </motion.div>
    </div>
  );
}

export default EditPage;
